import React from "react";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import Link from "next/link";
import { useRouter } from "next/router";

import { getCityStateGeocodeServerSide } from "api/requests/getCityStateGeocode";
import { getDeals } from "api/requests/getDeals";
import getPromotedStrains from "api/requests/getPromotedStrains";
import { getPromotions } from "api/requests/getPromotions";
import { Action, Category } from "constants/events";
import { Coordinates } from "custom-types/Coordinates";
import { Deal } from "custom-types/Deals";
import { PromotedStrain } from "custom-types/Directus";
import { Promotion } from "custom-types/Promotions";
import { useEventTracker } from "hooks/useEventTracker";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import { retrieveLocationUrl } from "utils/locationsUtils";
import logError from "utils/logError";

import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import CoinList from "components/CoinList";
import DealsDisclaimer from "components/DealsDisclaimer";
import DealsHero from "components/DealsHero";
import DealsResults from "components/DealsResults";
import MetaTags from "components/MetaTags";
import PhenotypeCarousel from "components/PhenotypeCarousel";
import PromoResults from "components/PromoResults";
import PromotedStrainCarousel from "components/StrainCarousel";

const DEALS_PER_PAGE = 6;

const extractLocation = (slug: string) => {
  const slugArr = slug.split("-");

  // length of 3 is city-state-country
  if (slugArr.length >= 3) {
    const state = slugArr[slugArr.length - 2].toUpperCase();
    const city = slugArr
      .slice(0, -2)
      .map((word: string) => capitalize(word))
      .join(" ");

    return { city, state };
  }

  // length of 2 is city-state or state-country
  if (slugArr.length === 2) {
    return { city: startCase(slugArr[0]), state: slugArr[1].toUpperCase() };
  }
};

/** if country is missing in slug, append it to the
 *  end to use in SEO canonical URL */
const formatSlug = (slug: string, countryCode: string) => {
  const countryCodes = ["us", "ca"];
  const slugArr = slug.split("-");

  const lastIndex = slugArr[slugArr.length - 1];
  if (lastIndex && !countryCodes.includes(lastIndex)) {
    slugArr.push(countryCode);
    return slugArr.join("-");
  }

  return slug;
};

type DealsLocationPageProps = {
  coordinates: Coordinates;
  countryCode: string;
  deals: { deals: Deal[]; totalCount: number; dealCategories: string[] };
  locationTitle: string;
  promotedStrains: PromotedStrain[];
  promotions: Promotion[];
};

const DealsLocationPage: React.FC<DealsLocationPageProps> = ({
  coordinates,
  countryCode,
  deals,
  locationTitle,
  promotedStrains,
  promotions,
}) => {
  const {
    query: { slug },
  } = useRouter();
  const { publishEvent } = useEventTracker();

  const isCanada = countryCode === "CA";

  const pageDescription = isCanada
    ? `Looking for weed deals ${locationTitle && `in ${locationTitle}`}? Find special sales, promo codes, coupons, and discounts on flower, carts, edibles, and dabs. Get the best dispensaries deals in your neighborhood on Leafly.`
    : `Looking for weed deals and discounts ${locationTitle && `in ${locationTitle}`}? Find special sales, promo codes, coupons, and discounts on flower, carts, edibles, and dabs. Get the best dispensaries deals in your neighborhood on Leafly`;

  return (
    <>
      <MetaTags
        title={
          isCanada
            ? `${locationTitle} Licensed Dispensary Deals Near Me | Leafly`
            : `${locationTitle} Dispensary Weed Deals & Discounts Near Me`
        }
        description={pageDescription}
        canonicalOverride={`/deals/${formatSlug(String(slug), countryCode)}`}
      />

      <div className="container mt-xl mb-xxl">
        <h1 data-testid="deals-header" className="mb-lg heading--l">
          {isCanada
            ? `Licensed dispensary deals in ${locationTitle} near me`
            : `Dispensary deals in ${locationTitle}`}
        </h1>

        <DealsHero className="mb-section" />

        {deals.totalCount > 0 && (
          <>
            <h2 className="heading--m mb-md"> Weed deals by products </h2>
            <CoinList
              className="mb-section"
              coins={deals.dealCategories}
              hrefLink={"/search/shop"}
              hrefLinkQueryParam={"&deal_active=true"}
              hrefProductCatQueryKey={"product_category"}
            />

            <h2 className="heading--m mb-md">Cannabis deals by strain type</h2>
            <PhenotypeCarousel className="mb-section" asLink linkToDeals />

            <PromotedStrainCarousel
              asLink
              className="mb-section"
              onClick={(strain) =>
                publishEvent({
                  action: Action.click,
                  category: Category.dealMenuStrains,
                  label: `strain name - ${strain.strain_name}`,
                })
              }
              promotedStrains={promotedStrains}
              title="Deals on top weed strains near you"
            />
          </>
        )}

        <DealsResults
          className="mb-section"
          coordinates={coordinates}
          initialDeals={deals.deals}
          totalCount={deals.totalCount}
        />

        <PromoResults className="mb-section" promotions={promotions} />

        <Link className="underline mb-section block" href="/deals/locations">
          See all locations
        </Link>

        <DealsDisclaimer />
      </div>
      <Breadcrumbs className="mt-xl">
        <Breadcrumb href="/deals">Deals</Breadcrumb>
        <Breadcrumb currentLocation href={`/deals/${slug}`}>
          {locationTitle}
        </Breadcrumb>
      </Breadcrumbs>
    </>
  );
};

export const getServerSideProps =
  createGetServerSideProps<DealsLocationPageProps>(
    async ({ query, resolvedUrl }) => {
      const locationSlug = query.slug as string;
      const location = extractLocation(locationSlug);

      if (!location) {
        return {
          notFound: true,
        };
      }

      const { city, state } = location;

      const { coordinates, countryCode, ...locationData } =
        await getCityStateGeocodeServerSide({
          city,
          state,
        }).catch(() => {
          logError(
            `Failed to determine coordinates for ${city}, ${state} in /deals/[slug] getServersideProps`,
          );

          return {
            city: undefined,
            coordinates: undefined,
            countryCode: undefined,
            stateLong: undefined,
          };
        });

      if (!coordinates || !countryCode) {
        return {
          notFound: true,
        };
      }
      const countryString = countryCode === "US" ? "united states" : "canada";
      const locationUrl = retrieveLocationUrl({
        city: locationData.city,
        countryString,
        state: locationData.stateLong,
      });

      if (locationUrl && resolvedUrl !== locationUrl) {
        return {
          redirect: {
            destination: locationUrl,
            statusCode: 301,
          },
        };
      }

      const [deals, promotedStrains, { promotions }] = await Promise.all([
        getDeals(coordinates, countryCode, 0, DEALS_PER_PAGE),
        getPromotedStrains(),
        getPromotions(coordinates, countryCode),
      ]);

      return {
        props: {
          coordinates,
          countryCode,
          deals,
          locationTitle: `${city}, ${state}`,
          promotedStrains,
          promotions,
        },
      };
    },
  );

export default DealsLocationPage;
